import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import { IonInput, ModalController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Subject, debounceTime, map, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { OrderType } from 'src/smoothr-web-app-core/enums/OrderType';
import { MapsUtils, calculateGeoDistance } from '../../../maps/utils/maps';
import { MapsUtils as MapUtils2 } from 'src/smoothr-web-app-core/utils/maps-utils';

import { Api } from '../../../smoothr-web-app-core/api/api';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import Address from '../../../smoothr-web-app-core/models/Address';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { TimeUtils } from '../../../smoothr-web-app-core/utils/time-utils';
import { isVenueOpen, sleep, venueAcceptsOrders } from '../../../smoothr-web-app-core/utils/utils';

import Map = google.maps.Map;
import MapOptions = google.maps.MapOptions;
import { OrderUtils } from 'src/smoothr-web-app-core/utils/order-utils';
import { ValidationUtils } from 'src/smoothr-web-app-core/utils/validation-utils';
import { MapPage } from 'src/app/pages/map/map.page';
import { NavigationService } from 'src/app/services/navigation.service';
import { CheckDeliveryRadiusModalComponent } from 'src/app/components/check-delivery-radius-modal/check-delivery-radius-modal.component';
import { AppComponent } from 'src/app/app.component';
import { FormControl } from '@angular/forms';
import { SearchCity } from 'src/smoothr-web-app-core/models/SearchCity';
import { AnalyticsService } from 'src/smoothr-web-app-core/services/analytics/analytics.service';
import { mapStyles } from './map-styles';
import { SwiperOptions } from 'swiper/types';
import { BeforeCreateOrderSelectTimeDialogComponent } from 'src/app/components/before-create-order-select-time-dialog/before-create-order-select-time-dialog.component';
import Table from 'src/smoothr-web-app-core/models/Table';

@Component({
	selector: 'app-map-venue',
	templateUrl: './map-venue.component.html',
	styleUrls: ['./map-venue.component.scss'],
})
export class MapVenueComponent extends RepositoryDirective implements OnInit, AfterViewInit, OnDestroy {
	@Input() showSearchInput = false;
	@Input() showVenueList = true;
	@Input() venueToFirstPositionShortId = '';

	@Output() selectType = new EventEmitter<PreorderType>();
	@ViewChild('mapContainer', { static: true })
	mapElement: ElementRef;
	@ViewChild('swiper') slides: ElementRef | undefined;
	map: Map;
	@ViewChild(IonInput, { static: true })
	inputField: IonInput;
	@ViewChild('venueList', { static: false }) venueList;
	public preorderTypes = [PreorderType.TAKE_AWAY, PreorderType.DELIVERY];
	mapOptions: MapOptions = {
		maxZoom: 15,
		minZoom: 5,
		disableDefaultUI: true,
		clickableIcons: false,
		styles: mapStyles,
	};
	clusterer: MarkerClusterer;
	loading = false;
	loadingAddress = false;
	predictions: any[] = [];
	showHint = false;
	searchTerm: string;
	allVenues: Venue[] = [];
	localVenues: Venue[] = [];
	selectedVenueForDelivery: Venue;
	showMap = true;
	slidesOpts: SwiperOptions = {
		direction: 'vertical',
		effect: 'slide',
		centeredSlides: true,
		slidesPerView: 'auto',
		grabCursor: true,
		spaceBetween: 6,
	};
	selectedVenue: Venue;
	MapsUtils = MapsUtils;
	private _showPredictions = false;

	preselectedShortId = '';
	preselectedPreorderType: PreorderType | null = null;

	queryWasChecked = false;

	showErrorSelectOrderType: boolean = false;
	pt = PreorderType;
	searchCityForm: FormControl = new FormControl('');
	private onDestroy$ = new Subject<void>();
	public cityPrediction: SearchCity[] = [];
	public promoCodeId: string = '';
	public addressForDelivery: Address;
	public searchControl = new FormControl('');
	public copyAllVenues: Venue[] = [];
	constructor(
		private snackbarCtrl: MatSnackBar,
		public repository: RepositoryService,
		private translate: TranslateService,
		private router: Router,
		private cdr: ChangeDetectorRef,
		private modalCtrl: ModalController,
		private route: ActivatedRoute,
		private navService: NavigationService,
		private popover: PopoverController,
		private analytics: AnalyticsService
	) {
		super(repository);
	}

	ngOnChanges() {}

	get relevantVenues(): Venue[] {
		return this.allVenues.filter(it => venueAcceptsOrders(it, PreorderType.TAKE_AWAY) || venueAcceptsOrders(it, PreorderType.INSIDE));
	}

	static navigate(router: Router) {
		return router.navigateByUrl(MapPage.url);
	}

	ngOnInit() {
		super.ngOnInit();
		this.searchCityForm.valueChanges.pipe(debounceTime(600), takeUntil(this.onDestroy$)).subscribe(v => {
			if (!v) {
				this.resetMap(true);
			} else {
				this.loadCity(v);
			}
		});
		this.map = new Map(this.mapElement.nativeElement, this.mapOptions);
		new Promise<void>(async resolve => {
			await this.fillInPlace(null);
			await this.initAutocomplete();
			resolve();
		});
		this.route.queryParams.subscribe(v => {
			if (v?.promoCodeId) {
				this.promoCodeId = v.promoCodeId;
			}
		});
		this.searchControl.valueChanges.pipe(debounceTime(500), takeUntil(this.onDestroy$)).subscribe(searchValue => {
			this.allVenues = this.copyAllVenues.filter(venue => {
				const copyValue = searchValue.toLowerCase();
				return (
					venue.name.toLowerCase().includes(copyValue) ||
					venue.readableId.toLowerCase().includes(copyValue) ||
					venue.city.de.toLowerCase().includes(copyValue) ||
					venue.postalCode.includes(copyValue) ||
					venue.street.toLowerCase().includes(copyValue)
				);
			});
			this.resetMap();
		});
		this.resetMap();

		this.route.queryParamMap.subscribe(query => {
			this.preselectedPreorderType = query.get('preorderType').toLocaleLowerCase() as PreorderType;
			this.preselectedShortId = query.get('short_id');
		});
	}
	ngAfterViewInit() {}

	async updateSwiperValue() {
		if (this.slides?.nativeElement) {
			const swiperElement = this.slides.nativeElement;
			console.log('AFTER VIEW', this.slides.nativeElement, swiperElement);

			if (swiperElement.swiperParams) {
				Object.assign(swiperElement, this.slidesOpts);
				await sleep(500);
				swiperElement.swiper.update();
			}
		}
	}
	async loadCity(search: string) {
		try {
			const city = await Api.loadCitiesFromService(search);
			this.cityPrediction = city;
		} catch (e) {}
	}
	ngOnDestroy(): void {
		this.onDestroy$.next();
	}
	async resetMap(ignoreExistingOfVenue: boolean = false) {
		if (this.allVenues.length === 0 || ignoreExistingOfVenue) {
			try {
				this.allVenues = (await Api.getAllVenues(environment.customerGroup)).data
					.filter(
						ven =>
							ven.isPublished &&
							(venueAcceptsOrders(ven, PreorderType.DELIVERY) || venueAcceptsOrders(ven, PreorderType.TAKE_AWAY))
					)
					.map(ven => {
						try {
							ven.openingHours = TimeUtils.sanitizeHours(ven.openingHours, ven.specialOpeningHours ?? []);
							ven.deliveryHours = TimeUtils.sanitizeHours(ven.deliveryHours);
						} catch (e) {
							console.error({
								message: 'Error while sanitizing hours ' + e,
								venue: ven.name + ' ' + ven.readableId,
								openingHours: ven.openingHours,
								deliveryHours: ven.deliveryHours,
							});
						}
						return ven;
					})
					.sort((a, b) => {
						const nameA = a?.name?.replace('Dean & David', '')?.trim();
						const nameB = b?.name?.replace('Dean & David', '')?.trim();
						return nameA?.localeCompare(nameB);
					});

				if (this.address) {
					this.allVenues = this.allVenues.sort((a, b) => {
						return (
							calculateGeoDistance(this.address.lat, this.address.lng, a.location.coordinates[1], a.location.coordinates[0]) -
							calculateGeoDistance(this.address.lat, this.address.lng, b.location.coordinates[1], b.location.coordinates[0])
						);
					});
				}
				this.copyAllVenues = this.allVenues;
				this.allVenues = this.allVenues.filter(it => it.readableId != 'dispatch_test_dnd_frei_komtur');
				this.updateSwiperValue();

				if (this.preselectedPreorderType && this.preselectedShortId && !this.queryWasChecked) {
					this.queryWasChecked = true;
					const selectedVenue = this.allVenues.find(
						ven => ven.readableId.toLocaleLowerCase() === this.preselectedShortId.toLocaleLowerCase()
					);
					if (selectedVenue) {
						console.log(this.preselectedPreorderType, this.preselectedShortId);

						await this.selectVenue(this.preselectedPreorderType, selectedVenue);
					}
				}
			} catch (e) {}
		}
		await this.setupMap(null);
	}
	public async showItem(selectedVenue: Venue, event: any) {
		// let isVenueOpenValue = true;
		// try {
		// 	isVenueOpenValue = isVenueOpen(selectedVenue);
		// } catch (e) {}
		// console.log('is venue open', isVenueOpenValue, selectedVenue.name);
		// const result = await ShowOnMapVenueDialogComponent.show(this.popover, {
		// 	title: selectedVenue.name,
		// 	event,
		// 	isVenueOpen: isVenueOpenValue,
		// });
		// console.log(result);
		// if (result) {
		this.selectVenue(undefined, selectedVenue);
		// }
	}
	async setupMap(selectedVenue: Venue) {
		if (this.loading) {
			return;
		}
		this.selectedVenue = selectedVenue;
		console.log(this.selectedVenue, selectedVenue);
		this.loading = true;
		this.cdr.detectChanges();
		this.clusterer = MapsUtils.addVenuesToMap(
			this.clusterer,
			selectedVenue,
			this.relevantVenues,
			this.map,
			this.popover,
			venue => this.setupMap(venue),
			(selectedVenue, event) => this.showItem(selectedVenue, event)
		);
		if (selectedVenue && selectedVenue.location && selectedVenue.location.coordinates) {
			const selectedIndex = this.relevantVenues.findIndex(ven => ven._id === selectedVenue._id);
			// wait until slides rendered
			this.scrollToVenue(selectedIndex ?? 0);

			await this.slides.nativeElement.swiper.slideTo(selectedIndex);
		}
		this.loading = false;
		this.cdr.detectChanges();
	}

	hidePredictions() {
		setTimeout(() => {
			this._showPredictions = false;
		}, 200);
	}

	showPredictions() {
		this._showPredictions = true;
	}

	async executeSearch() {
		console.log('executeSearch()');
		this.loading = true;
		try {
			const result = await MapUtils2.executeSearch(this.inputField);
			await this.fillInPlace(result);
		} catch (e) {}
		this.loading = false;
	}

	resetSearch() {
		console.log('resetSearch()');
		this.searchTerm = '';
		this.predictions = [];
		this.selectedVenueForDelivery = null;
		this.selectedVenue = null;
		if (this.address) {
			this.repository.address.emit(null);
		}
		this.showHint = false;
		this.cdr.detectChanges();
	}

	async fillInPlace(address: Address) {
		console.log('fillInPlace()');
		if (!address) {
			return;
		}
		this.loading = true;
		try {
			this.searchTerm = MapsUtils.checkAddress(address);
			this.loading = false;
			if (this.address !== address) {
				this.repository.address.emit(address);
			}
			await this.onSlideChange();
		} catch (e) {
			console.error(e);
			this.snackbarCtrl.open(this.translate.instant(e), null, {
				duration: 2000,
			});
		}
		this.loading = false;
	}

	async loadVenues(address: Address) {
		if (this.loading) {
			return;
		}
		this.loading = true;
		this.cdr.detectChanges();
		this.localVenues = [];
		try {
			this.localVenues = (await this.repository.getVenuesByAddress(environment.customerGroup, address)).filter(it =>
				venueAcceptsOrders(it, PreorderType.DELIVERY)
			);
			if (this.address) {
				this.localVenues = this.localVenues.sort((a, b) => {
					return (
						calculateGeoDistance(this.address.lat, this.address.lng, a.location.coordinates[1], a.location.coordinates[0]) -
						calculateGeoDistance(this.address.lat, this.address.lng, b.location.coordinates[1], b.location.coordinates[0])
					);
				});
			}
			this.searchTerm = MapsUtils.addressToString(address);
			console.log('LOCAL', this.localVenues);
			if (this.localVenues.length === 0) {
				this.snackbarCtrl.open(
					this.translate.instant('map_page.no_venues_in_address', {
						title: this.searchTerm,
					}),
					null,
					{
						duration: 2000,
					}
				);
				this.loading = false;
				await this.resetMap();
				this.cdr.detectChanges();
				return;
			}
			if (this.selectedVenueForDelivery) {
				const deliveryVenues = this.localVenues.filter(ven => venueAcceptsOrders(ven, PreorderType.DELIVERY));
				const selectedDeliveryVenue = deliveryVenues.find(ven => ven._id === this.selectedVenueForDelivery._id);
				if (selectedDeliveryVenue) {
					await this.selectVenue(PreorderType.DELIVERY, selectedDeliveryVenue);
				} else {
				}
				this.selectedVenueForDelivery = null;
				return;
			}
			this.selectedVenue = null;
			this.loading = false;
			await this.setupMap(this.selectedVenue);
		} catch (e) {
			console.error(e);
		}
		this.loading = false;
		this.cdr.detectChanges();
	}

	async onSlideChange() {
		let index = await this.slides?.nativeElement.swiper.activeIndex;
		console.log('onSlide change', index);

		if (index >= this.relevantVenues.length) {
			index = 0;
		}
		await this.setupMap(this.relevantVenues[index]);
	}

	async selectVenue(
		preorderType: PreorderType = PreorderType.TAKE_AWAY,
		venue: Venue,
		table?: Table,
		attempt: number = 0,
		prevError: any = null
	) {
		this.showErrorSelectOrderType = false;
		if (!preorderType) {
			this.showErrorSelectOrderType = true;
			return;
		}
		if (!venueAcceptsOrders(venue, preorderType)) {
			return;
		}
		if (preorderType === PreorderType.DELIVERY && !this.address) {
			await this.showDialogForAddress();
			this.selectedVenueForDelivery = venue;
			this.showHint = true;
			return;
		}

		if (attempt > 5) {
			this.loading = false;
			this.snackbarCtrl.open(prevError, null, {
				duration: 2000,
			});
			return;
		}
		this.loading = true;

		try {
			await this.repository.getVenue(venue._id);
			this.repository.createOrder(venue, this.address, OrderType.PREORDER, preorderType);

			if (table) {
				this.order.table = table._id;
				this.order.tableNumber = table.number;
			}

			let isOpen = true;
			try {
				isOpen = isVenueOpen(venue);
			} catch (e) {}

			if (!table) {
				const result = await BeforeCreateOrderSelectTimeDialogComponent.show(this.modalCtrl, this.order, venue, preorderType);

				if (!result) {
					// this.snackbarCtrl.open('Empty slots');
					this.loading = false;
					return;
				}
				if (result.asap) {
					this.order.asap = true;
				}
				if (result.slot) {
					this.order.asap = false;
					this.order.orderAt = result.slot;
				}
			} else {
				this.order.asap = true;
			}

			this.repository.onOrderChange(this.order);

			await sleep(1000);
			if (AppComponent.isNativeApp) {
				this.navService.navigateSelectedCategory();
			} else {
				if (preorderType === PreorderType.FOOD_SPOT) {
					this.navService.foodSpot();
				} else {
					this.navService.menu();
				}
			}
			this.loading = false;
		} catch (e) {
			await this.selectVenue(preorderType, venue, table, attempt + 1, e);
		}
	}

	async initAutocomplete() {
		try {
			// await MapUtils2.initAutocompleteCity(
			// 	this.inputField,
			// 	predictions => {
			// 		console.log('PREV', predictions);
			// 		this.predictions = predictions as any[];
			// 		this.cdr.detectChanges();
			// 	}
			// 	// loading => (this.loadingAddress = loading)
			// );
		} catch (e) {
			console.log(e);
		}

		this.loading = false;
	}

	onAddress() {
		super.onAddress();
		new Promise(async () => {
			if (this.address) {
				await this.loadVenues(this.address);
			} else {
				await this.resetMap();
				// this.resetSearch();
			}
		});
	}

	setShowMap(value: boolean) {
		this.showMap = value;
		this.cdr.detectChanges();
	}
	mapPrediction(value: any) {
		console.log(value);
		return 'test';
	}
	async checkAddressDelivery(venue: Venue, address: Address) {
		if (this.address.number && this.address.city && this.address.street) {
			try {
				const addressValidationResult = ValidationUtils.validateAddress(address, true);
				if (addressValidationResult) {
					return false;
				}
				if (venue.deliveryByAreas && venue?.deliveryAreas?.length > 0) {
					return true;
				}
				const delivers =
					(venue.deliveryByRadius &&
						calculateGeoDistance(address.lat, address.lng, venue.location.coordinates[1], venue.location.coordinates[0]) <=
							venue.deliveryRadius) ||
					(venue.isPostalDelivery && venue.deliveryPostalCodes.indexOf(address.postalCode));

				if (!delivers) {
					this.snackbarCtrl.open(this.translate.instant('errors.street_delivery'), null, {
						duration: 2000,
					});
					return false;
				}

				return true;
			} catch (e) {
				console.log(e);

				return false;
			}
		} else {
			return false;
		}
	}
	private async scrollToVenue(index: number) {
		try {
			await this.venueList.scrollToPoint(0, this.venueList.el.children[index].offsetTop - 250, 1000);
		} catch (e) {}
	}
	changeType(item: PreorderType) {
		this.showErrorSelectOrderType = false;
		this.selectType.emit(item);
		if (item === PreorderType.TAKE_AWAY) {
			this.addressForDelivery = null;
		}

		if (item == PreorderType.DELIVERY) {
			this.showDialogForAddress();
		}
		this.updateSwiperValue();
	}
	private async showDialogForAddress() {
		const result = await CheckDeliveryRadiusModalComponent.show(this.modalCtrl, this.address);
		if (result?.address) {
			this.addressForDelivery = result?.address;
			this.repository.address.emit(result.address);
		}
		this.updateSwiperValue();
	}

	clearDeliveryAddress() {
		this.addressForDelivery = null;
		this.showDialogForAddress();
	}

	goHome() {
		this.navService.home();
	}

	updateSearch($event: string) {
		this.searchControl.patchValue($event);
	}
}
